import { sql } from "drizzle-orm";

import type { WsMembershipAvailability } from "@storyboard/web-client";
import { bulkInsert } from "@/db/bulkInsert";
import { wsMembershipAvailability } from "@/db/schema";
import type { MembershipAvailability } from "@/db/types";
import { deduplicateByKeyValue } from "@/models/appSyncFunctions.ts";
import { emitTableUpdates } from "@/models/eventing/TableEventManager";
import { isWorkspaceMembershipOffline, offlineThreshold, statusType } from "@/utils";

export function transformWsMembershipAvailability(record: WsMembershipAvailability): MembershipAvailability {
  const mostRecentVolumeAudible = record?.mostRecentVolume?.audible === true;
  const status = statusType(record.calculatedAvailability);
  const handsFreeUpdatedAt = record?.mostRecentHandsFreeStatus?.updatedAt;
  const offline = isWorkspaceMembershipOffline({
    handsFreeUpdatedAt,
    offlineThreshold,
  });
  return {
    workspaceMembershipId: record.workspaceMembershipId,
    workspaceId: record.workspaceId,
    calculatedAvailability: record.calculatedAvailability,
    mostRecentAvailabilityChange: record.mostRecentAvailabilityChange,
    mostRecentHandsFreeStatusEnabled: record.mostRecentHandsFreeStatus?.enabled,
    mostRecentHandsFreeStatusChangedAt: record.mostRecentHandsFreeStatus?.changedAt,
    mostRecentHandsFreeStatusUpdatedAt: record.mostRecentHandsFreeStatus?.updatedAt,
    mostRecentVolumeVolume: record.mostRecentVolume?.volume,
    mostRecentVolumeAudible,
    mostRecentVolumeChangedAt: record.mostRecentVolume?.changedAt,
    mostRecentVolumeUpdatedAt: record.mostRecentVolume?.updatedAt,
    mostRecentDutyStatusStatus: record.mostRecentDutyStatus?.status,
    mostRecentDutyStatusSource: record.mostRecentDutyStatus?.source,
    mostRecentDutyStatusChangedAt: record.mostRecentDutyStatus?.changedAt,
    mostRecentDutyStatusUpdatedAt: record.mostRecentDutyStatus?.updatedAt,
    mostRecentDrivingStatus: record.mostRecentDrivingStatus?.status,
    mostRecentDrivingStatusChangedAt: record.mostRecentDrivingStatus?.changedAt,
    mostRecentDrivingStatusUpdatedAt: record.mostRecentDrivingStatus?.updatedAt,
    mostRecentDeviceLocationLatitude: record.mostRecentDeviceLocation?.latitude,
    mostRecentDeviceLocationLongitude: record.mostRecentDeviceLocation?.longitude,
    mostRecentDeviceLocationChangedAt: record.mostRecentDeviceLocation?.changedAt,
    mostRecentDeviceLocationUpdatedAt: record.mostRecentDeviceLocation?.updatedAt,
    mostRecentDeviceLocationNearestAddressMunicipality: record.mostRecentDeviceLocation?.nearestAddress?.municipality,
    mostRecentDeviceLocationNearestAddressRegion: record.mostRecentDeviceLocation?.nearestAddress?.region,
    mostRecentDeviceLocationNearestAddressCountry: record.mostRecentDeviceLocation?.nearestAddress?.country,
    updatedAt: record.updatedAt,
    status,
    offline,
  };
}
export async function bulkUpsertMembershipAvailability(records: MembershipAvailability[]) {
  if (records.length === 0) return;
  const deduped = deduplicateByKeyValue(records, (item) => item.workspaceMembershipId);
  await bulkInsert(wsMembershipAvailability, deduped, {
    onConflict: {
      type: "doUpdate",
      target: [wsMembershipAvailability.workspaceMembershipId],
      set: {
        calculatedAvailability: sql`"excluded"."calculatedAvailability"`,
        mostRecentAvailabilityChange: sql`"excluded"."mostRecentAvailabilityChange"`,
        mostRecentHandsFreeStatusEnabled: sql`"excluded"."mostRecentHandsFreeStatusEnabled"`,
        mostRecentHandsFreeStatusChangedAt: sql`"excluded"."mostRecentHandsFreeStatusChangedAt"`,
        mostRecentHandsFreeStatusUpdatedAt: sql`"excluded"."mostRecentHandsFreeStatusUpdatedAt"`,
        mostRecentVolumeVolume: sql`"excluded"."mostRecentVolumeVolume"`,
        mostRecentVolumeChangedAt: sql`"excluded"."mostRecentVolumeChangedAt"`,
        mostRecentVolumeUpdatedAt: sql`"excluded"."mostRecentVolumeUpdatedAt"`,
        mostRecentVolumeAudible: sql`"excluded"."mostRecentVolumeAudible"`,
        mostRecentDutyStatusStatus: sql`"excluded"."mostRecentDutyStatusStatus"`,
        mostRecentDutyStatusSource: sql`"excluded"."mostRecentDutyStatusSource"`,
        mostRecentDutyStatusChangedAt: sql`"excluded"."mostRecentDutyStatusChangedAt"`,
        mostRecentDutyStatusUpdatedAt: sql`"excluded"."mostRecentDutyStatusUpdatedAt"`,
        mostRecentDrivingStatus: sql`"excluded"."mostRecentDrivingStatus"`,
        mostRecentDrivingStatusChangedAt: sql`"excluded"."mostRecentDrivingStatusChangedAt"`,
        mostRecentDrivingStatusUpdatedAt: sql`"excluded"."mostRecentDrivingStatusUpdatedAt"`,
        mostRecentDeviceLocationLatitude: sql`"excluded"."mostRecentDeviceLocationLatitude"`,
        mostRecentDeviceLocationLongitude: sql`"excluded"."mostRecentDeviceLocationLongitude"`,
        mostRecentDeviceLocationChangedAt: sql`"excluded"."mostRecentDeviceLocationChangedAt"`,
        mostRecentDeviceLocationUpdatedAt: sql`"excluded"."mostRecentDeviceLocationUpdatedAt"`,
        mostRecentDeviceLocationNearestAddressMunicipality: sql`"excluded"."mostRecentDeviceLocationNearestAddressMunicipality"`,
        mostRecentDeviceLocationNearestAddressRegion: sql`"excluded"."mostRecentDeviceLocationNearestAddressRegion"`,
        mostRecentDeviceLocationNearestAddressCountry: sql`"excluded"."mostRecentDeviceLocationNearestAddressCountry"`,
        updatedAt: sql`"excluded"."updatedAt"`,
        offline: sql`"excluded"."offline"`,
        status: sql`"excluded"."status"`,
      },
    },
  });
  emitTableUpdates(["ws_membership_availability"]);
}
export async function bulkUpsertWsMembershipAvailability(records: WsMembershipAvailability[]) {
  await bulkUpsertMembershipAvailability(records.map(transformWsMembershipAvailability));
}
